// src/features/auth/authSlice.ts

import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import * as authService from '../../api/authService';
import * as userService from '../../api/userService';
import {
    // Payloads
    LoginPayload,
    RegisterPayload,
    GoogleSignInPayload,
    RequestPasswordResetPayload,
    ConfirmPasswordResetPayload,
    ResetPasswordPayload, // Renamed from ChangePasswordPayload
    // User Management
    UpdateProfilePayload,
    UpdatePreferencesPayload,
    // Responses and Data Types
    LoginResponseData,
    RegisterResponseData,
    GoogleSignInResponseData,
    User
} from '../../types';

interface AuthState {
    user: User | null;
    token: string | null;
    isAuthenticated: boolean;
    status: {
        auth: 'idle' | 'loading' | 'succeeded' | 'failed';
        user: 'idle' | 'loading' | 'succeeded' | 'failed';
    };
    message: string | null;
    error: string | null;
}

const token = localStorage.getItem('token');

const initialState: AuthState = {
    user: null,
    token: token,
    isAuthenticated: !!token,
    status: {
        auth: 'idle',
        user: 'idle',
    },
    message: null,
    error: null,
};

// --- Helper function to process user data from API ---
const processUserData = (userData: any): User => ({
    ...userData,
    isAdFree: !!userData.isAdFree,
    isGoogleLinked: !!userData.isGoogleLinked,
    isSubscribed: !!userData.isSubscribed,
    // Ensure timestamps from bigint are handled as numbers
    adFreeUntil: userData.adFreeUntil ? Number(userData.adFreeUntil) : null,
    subscriptionExpiresAt: userData.subscriptionExpiresAt ? Number(userData.subscriptionExpiresAt) : null,
});


// ================================================================================================
// ASYNC THUNKS
// ================================================================================================

export const loginUser = createAsyncThunk<LoginResponseData, LoginPayload, { rejectValue: string }>(
    'auth/login',
    async (payload, { rejectWithValue }) => {
        try {
            return await authService.login(payload);
        } catch (error: any) {
            return rejectWithValue(error.message || 'Login failed.');
        }
    }
);

export const registerUser = createAsyncThunk<RegisterResponseData, RegisterPayload, { rejectValue: string }>(
    'auth/register',
    async (payload, { rejectWithValue }) => {
        try {
            return await authService.register(payload);
        } catch (error: any) {
            return rejectWithValue(error.message || 'Registration failed.');
        }
    }
);

export const googleLoginUser = createAsyncThunk<GoogleSignInResponseData, GoogleSignInPayload, { rejectValue: string }>(
    'auth/googleLogin',
    async (payload, { rejectWithValue }) => {
        try {
            return await authService.googleSignIn(payload);
        } catch (error: any) {
            return rejectWithValue(error.message || 'Google Sign-In failed.');
        }
    }
);

export const fetchUserInfo = createAsyncThunk<User, void, { rejectValue: string }>(
    'auth/fetchUserInfo',
    async (_, { dispatch, rejectWithValue }) => {
        try {
            const userData = await userService.getUserInfo();
            return processUserData(userData);
        } catch (error: any) {
            dispatch(logout());
            return rejectWithValue('Your session has expired. Please log in again.');
        }
    }
);

export const updateUserProfile = createAsyncThunk<User, UpdateProfilePayload, { rejectValue: string }>(
    'auth/updateUserProfile',
    async (payload, { rejectWithValue }) => {
        try {
            // This now also handles setting pre-defined avatar URLs
            const userData = await userService.updateUserProfile(payload);
            return processUserData(userData);
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to update profile.');
        }
    }
);

// --- NEW: AVATAR MANAGEMENT THUNKS ---

/**
 * Uploads a cropped avatar image file.
 */
export const uploadAvatar = createAsyncThunk<User, FormData, { rejectValue: string }>(
    'auth/uploadAvatar',
    async (formData, { rejectWithValue }) => {
        try {
            const updatedUser = await userService.uploadUserAvatar(formData);
            return processUserData(updatedUser);
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.message || 'Failed to upload avatar.');
        }
    }
);

/**
 * Removes the user's custom avatar and reverts to the default.
 */
export const removeAvatar = createAsyncThunk<User, void, { rejectValue: string }>(
    'auth/removeAvatar',
    async (_, { rejectWithValue }) => {
        try {
            const updatedUser = await userService.removeUserAvatar();
            return processUserData(updatedUser);
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.message || 'Failed to remove avatar.');
        }
    }
);

// --- END OF NEW THUNKS ---


export const updateUserPreferences = createAsyncThunk<User, UpdatePreferencesPayload, { rejectValue: string }>(
    'auth/updateUserPreferences',
    async (payload, { rejectWithValue }) => {
        try {
            const userData = await userService.updateUserPreferences(payload);
            return processUserData(userData);
        } catch (error: any) {
            console.error('Failed to sync preferences:', error.message);
            return rejectWithValue('Failed to update preferences.');
        }
    }
);

export const requestPasswordReset = createAsyncThunk<void, RequestPasswordResetPayload, { rejectValue: string }>(
    'auth/requestPasswordReset',
    async (payload, { rejectWithValue }) => {
        try {
            await authService.requestPasswordReset(payload);
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to request password reset.');
        }
    }
);

export const confirmPasswordReset = createAsyncThunk<void, ConfirmPasswordResetPayload, { rejectValue: string }>(
    'auth/confirmPasswordReset',
    async (payload, { rejectWithValue }) => {
        try {
            await authService.confirmPasswordReset(payload);
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to confirm password reset.');
        }
    }
);

export const resetPasswordWithOld = createAsyncThunk<void, ResetPasswordPayload, { rejectValue: string }>(
    'auth/resetPasswordWithOld',
    async (payload, { rejectWithValue }) => {
        try {
            await authService.resetPasswordWithOldPassword(payload);
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to change password.');
        }
    }
);

export const deleteAccountThunk = createAsyncThunk<void, void, { rejectValue: string }>(
    'auth/deleteAccount',
    async (_, { dispatch, rejectWithValue }) => {
        try {
            await authService.deleteAccount();
            dispatch(logout()); // Immediately log out on the client side
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to delete account.');
        }
    }
);


// ================================================================================================
// THE SLICE
// ================================================================================================

const authSlice = createSlice({
    name: 'auth',
    initialState,
    reducers: {
        logout: (state) => {
            // authService.logout().catch(err => console.error("Logout API call failed:", err));
            state.user = null;
            state.token = null;
            state.isAuthenticated = false;
            state.status = { auth: 'idle', user: 'idle' };
            state.error = null;
            state.message = null;
            localStorage.removeItem('token');
            // Also clear other related local storage if any
            localStorage.removeItem('themeMode');
        },
        clearAuthStatus: (state) => {
            state.status = { auth: 'idle', user: 'idle' };
            state.error = null;
            state.message = null;
        }
    },
    extraReducers: (builder) => {
        const handleAuthPending = (state: AuthState) => {
            state.status.auth = 'loading';
            state.error = null;
            state.message = null;
        };

        const handleAuthFailed = (state: AuthState, action: any) => {
            state.status.auth = 'failed';
            state.error = action.payload || action.error.message || 'An unknown authentication error occurred.';
        };

        const handleUserPending = (state: AuthState) => {
            state.status.user = 'loading';
            state.error = null;
            state.message = null;
        };

        const handleUserFailed = (state: AuthState, action: any) => {
            state.status.user = 'failed';
            state.error = action.payload || action.error.message || 'An unknown user data error occurred.';
        };

        builder
            // --- Login Flow ---
            .addCase(loginUser.pending, handleAuthPending)
            .addCase(loginUser.fulfilled, (state, action: PayloadAction<LoginResponseData>) => {
                state.status.auth = 'succeeded';
                state.token = action.payload.SessionToken;
                state.isAuthenticated = true;
                localStorage.setItem('token', action.payload.SessionToken);
                // Note: The `user` object is not set here. `fetchUserInfo` must be dispatched next.
            })
            .addCase(loginUser.rejected, handleAuthFailed)

            // --- Register & Google Sign-In Flows (Optimized to set user data immediately) ---
            .addCase(registerUser.pending, handleAuthPending)
            .addCase(googleLoginUser.pending, handleAuthPending)

            // --- User Info Fetch Flow ---
            .addCase(fetchUserInfo.pending, handleUserPending)
            .addCase(fetchUserInfo.fulfilled, (state, action: PayloadAction<User>) => {
                state.status.user = 'succeeded';
                state.user = action.payload;
            })
            .addCase(fetchUserInfo.rejected, handleUserFailed)

            // --- Profile & Preferences Update Flow ---
            .addCase(updateUserProfile.pending, handleUserPending)
            .addCase(updateUserProfile.fulfilled, (state, action: PayloadAction<User>) => {
                state.status.user = 'succeeded';
                state.user = action.payload; // Update user state with fresh data
                state.message = 'Profile updated successfully.';
            })
            .addCase(updateUserProfile.rejected, handleUserFailed)

            // --- NEW: AVATAR MANAGEMENT REDUCERS ---
            .addCase(uploadAvatar.pending, handleUserPending)
            .addCase(uploadAvatar.fulfilled, (state, action: PayloadAction<User>) => {
                state.status.user = 'succeeded';
                state.user = action.payload;
                state.message = 'Avatar updated successfully.';
            })
            .addCase(uploadAvatar.rejected, handleUserFailed)

            .addCase(removeAvatar.pending, handleUserPending)
            .addCase(removeAvatar.fulfilled, (state, action: PayloadAction<User>) => {
                state.status.user = 'succeeded';
                state.user = action.payload;
                state.message = 'Avatar removed successfully.';
            })
            .addCase(removeAvatar.rejected, handleUserFailed)

            // --- END OF NEW REDUCERS ---

            .addCase(updateUserPreferences.pending, handleUserPending)
            .addCase(updateUserPreferences.fulfilled, (state, action: PayloadAction<User>) => {
                state.status.user = 'succeeded';
                if (state.user) {
                    state.user.preferences = action.payload.preferences;
                }
                state.message = 'Preferences updated successfully.'; // Added to trigger success alert
            })
            .addCase(updateUserPreferences.rejected, handleUserFailed)

            // --- Password Reset & Change Flows ---
            .addCase(requestPasswordReset.pending, handleAuthPending)
            .addCase(requestPasswordReset.fulfilled, (state) => {
                state.status.auth = 'succeeded';
                state.message = 'If an account with this email exists, a password reset link has been sent.';
            })
            .addCase(requestPasswordReset.rejected, handleAuthFailed)

            .addCase(confirmPasswordReset.pending, handleAuthPending)
            .addCase(confirmPasswordReset.fulfilled, (state) => {
                state.status.auth = 'succeeded';
                state.message = 'Your password has been successfully reset. Please log in.';
            })
            .addCase(confirmPasswordReset.rejected, handleAuthFailed)

            .addCase(resetPasswordWithOld.pending, handleAuthPending)
            .addCase(resetPasswordWithOld.fulfilled, (state) => {
                state.status.auth = 'succeeded';
                state.message = 'Password changed successfully.';
            })
            .addCase(resetPasswordWithOld.rejected, handleAuthFailed)

            // -- Account Deletion ---
            .addCase(deleteAccountThunk.pending, handleAuthPending)
            .addCase(deleteAccountThunk.rejected, handleAuthFailed)

            .addMatcher(
                (action) => [registerUser.fulfilled.type, googleLoginUser.fulfilled.type].includes(action.type),
                (state, action: PayloadAction<RegisterResponseData | GoogleSignInResponseData>) => {
                    state.status.auth = 'succeeded';
                    state.token = action.payload.SessionToken;
                    state.isAuthenticated = true;
                    localStorage.setItem('token', action.payload.SessionToken);
                }
            )
            .addMatcher(
                (action) => [registerUser.rejected.type, googleLoginUser.rejected.type].includes(action.type),
                handleAuthFailed
            );
    },
});

export const { logout, clearAuthStatus } = authSlice.actions;

export default authSlice.reducer;